/**
 * Organizations: types and the pure rules the UI shares with the database.
 * The database is the control (20261007100000); these mirror its checks so
 * a form can fail in place instead of after a round trip. Pure; adapters do
 * the I/O.
 */
import type { ContentI18n } from "@/lib/questContent/types";
import type { ViewTier } from "@/lib/tier";

export type OrgKind = "ngo" | "business" | "community" | "other";
export const ORG_KINDS: readonly OrgKind[] = ["ngo", "business", "community", "other"];

/**
 * Per-org levels (20261011…, org member levels): Simple < Medio < Admin <
 * Owner. "Medio" is stored as `member` (only the label changed), so rows and
 * notifications written before the ladder keep working.
 */
export type OrgRole = "owner" | "admin" | "member" | "simple";
/** A level an invite or a role change can hand out — never Owner. */
export type InviteRole = Exclude<OrgRole, "owner">;
/** Link invites admit as Simple only (accept_org_invite lowers anything else). */
export type LinkInviteRole = Extract<InviteRole, "simple">;

export interface Organization {
  id: string;
  slug: string;
  name: string;
  kind: OrgKind;
  bio: string | null;
  website: string | null;
  locationCity: string | null;
  locationCountry: string | null;
  logoUrl: string | null;
  bannerUrl: string | null;
  logoPath: string | null;
  bannerPath: string | null;
  verified: boolean;
  createdAt: string;
}

/**
 * The org a quest was posted as (organizations PR 2), as a card shows it.
 * Built from organizations' directory columns, which anon can read too.
 */
export interface PostedAsOrg {
  slug: string;
  name: string;
  logoUrl: string | null;
  verified: boolean;
}

/** An open quest an org posted, as its public page lists it. */
export interface OrgOpenQuest {
  id: string;
  title: string;
  status: string;
  deadline: string | null;
  totalBudgetUsd: number;
  currency: string;
  createdAt: string;
  sourceLang: string | null;
  i18n: ContentI18n | null;
}

export interface OrgMember {
  userId: string;
  role: OrgRole;
  joinedAt: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
}

/** An org the viewer belongs to, with their role in it. */
export interface MyOrganization extends Organization {
  myRole: OrgRole;
}

/** What move_quest_to_organization changed (20261021150000). */
export interface MoveQuestResult {
  movedQuests: number;
  reissuedCertificates: number;
}

/** Orgs a creator may move a personal quest into: owner or admin only. */
export function orgsForQuestMove(orgs: readonly MyOrganization[]): MyOrganization[] {
  return sortOrgsByName(orgs.filter((o) => canManageMembers(o.myRole)));
}

/**
 * Where this viewer can move this quest: nowhere unless they created it and it
 * is still personal (move_quest_to_organization re-checks all of it).
 */
export function moveTargetsForQuest(
  quest: { isCreator: boolean; organizationId: string | null | undefined },
  orgs: readonly MyOrganization[],
): MyOrganization[] {
  if (!quest.isCreator || quest.organizationId) return [];
  return orgsForQuestMove(orgs);
}

export type InviteState = "invited" | "joined" | "expired" | "revoked";

/** Who a `user` invite is for: public-profile fields, org admins only. */
export interface OrgInvitee {
  userId: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
}

export interface OrgInvite {
  id: string;
  /** `user` = an existing weXare account picked by name or @handle. */
  kind: "link" | "email" | "user";
  role: InviteRole;
  emailHint: string | null;
  /** Present for org admins, and for the invite's own creator (org_invite_list masks it otherwise). */
  code: string | null;
  /** `user` invites only, for org admins or the invite's creator (org_invite_list masks it). */
  invitee: OrgInvitee | null;
  createdAt: string;
  expiresAt: string;
  state: InviteState;
}

/** A search hit in the "Invite people on weXare" picker. */
export interface InvitableUser {
  userId: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  isMember: boolean;
  /**
   * The person's weXare tier (Admin by role, else profiles.tier). Only a
   * platform admin can read other people's profiles rows, so it is set for
   * them alone; undefined means "not visible to you", never "simple".
   */
  tier?: ViewTier;
}

export const USER_SEARCH_MIN_CHARS = 2;
export const USER_SEARCH_LIMIT = 8;

/**
 * The text a people search runs on, or null when it is too short to send.
 * Drops a leading "@" (people paste handles) and every character that would
 * break a PostgREST `or=(…)` filter or act as a LIKE wildcard, so the term
 * can be interpolated as-is.
 */
export function userSearchTerm(query: string): string | null {
  const term = query.trim().replace(/^@+/, "").replace(/[,()"'\\*%:.]/g, " ").replace(/\s+/g, " ").trim();
  return term.length >= USER_SEARCH_MIN_CHARS ? term : null;
}

/**
 * Order hits so the likeliest person comes first: an exact @handle, then a
 * handle or name that starts with the term, then the rest by name.
 */
export function rankInvitableUsers(users: InvitableUser[], term: string): InvitableUser[] {
  const q = term.toLowerCase();
  const score = (u: InvitableUser) => {
    const handle = u.handle?.toLowerCase() ?? "";
    const name = u.displayName?.toLowerCase() ?? "";
    if (handle === q) return 0;
    if (handle.startsWith(q) || name.startsWith(q)) return 1;
    return 2;
  };
  return [...users].sort((a, b) =>
    score(a) - score(b) || (a.displayName ?? a.handle ?? "").localeCompare(b.displayName ?? b.handle ?? ""));
}

export interface PendingOrgInvite {
  inviteId: string;
  organizationId: string;
  slug: string;
  name: string;
  logoUrl: string | null;
  verified: boolean;
  role: InviteRole;
  invitedByName: string | null;
  expiresAt: string;
}

/** What a link code points at — read-only, shown before the user taps Join. */
export interface OrgInvitePreview {
  organizationId: string;
  slug: string;
  name: string;
  logoUrl: string | null;
  verified: boolean;
}

export interface OrgJoinRequest {
  id: string;
  organizationId: string;
  userId: string;
  message: string | null;
  status: "pending" | "approved" | "rejected" | "cancelled";
  createdAt: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
}

export type PresenceBucket = "active_now" | "recent" | "hours" | "days" | "long_ago" | "hidden";

export interface MemberPresence {
  bucket: PresenceBucket;
  amount: number | null;
}

export interface CreateOrganizationInput {
  name: string;
  slug: string;
  kind: OrgKind;
  bio?: string | null;
  website?: string | null;
  locationCity?: string | null;
  locationCountry?: string | null;
}

export type UpdateOrganizationInput = Partial<
  Pick<CreateOrganizationInput, "name" | "kind" | "bio" | "website" | "locationCity" | "locationCountry">
>;

export interface AdminOrganizationRow {
  id: string;
  slug: string;
  name: string;
  kind: OrgKind;
  website: string | null;
  logoUrl: string | null;
  locationCity: string | null;
  locationCountry: string | null;
  createdAt: string;
  verifiedAt: string | null;
  verificationNote: string | null;
  memberCount: number;
  ownerHandle: string | null;
  ownerEmailMatchesWebsite: boolean;
}

// ─── Names and slugs ────────────────────────────────────────────────────────
/** Same rule as the organizations.name CHECK: 2–80 characters once trimmed. */
export function isValidOrgName(name: string): boolean {
  const n = name.trim().length;
  return n >= 2 && n <= 80;
}


/** Mirrors create_organization's reserved list. */
export const RESERVED_ORG_SLUGS: ReadonlySet<string> = new Set([
  "admin", "new", "manage", "settings", "join", "create",
  "edit", "api", "help", "support", "wexare", "official",
]);

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

/** Same rule as the organizations.slug CHECK, plus the reserved list. */
export function isValidOrgSlug(slug: string): boolean {
  return SLUG_RE.test(slug) && !slug.includes("--") && !RESERVED_ORG_SLUGS.has(slug);
}

/** A valid slug derived from a name: accents folded, words dashed, ≤ 40 chars. */
export function suggestOrgSlug(name: string): string {
  let s = name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
  if (s.length < 3) s = `${s || "org"}-org`.replace(/^-/, "");
  if (RESERVED_ORG_SLUGS.has(s)) s = `${s}-org`;
  return s;
}

// ─── Website ────────────────────────────────────────────────────────────────
/**
 * The column takes https only. Returns the normalized URL, null for "no
 * website", or undefined when the input cannot be a website.
 */
export function normalizeWebsite(input: string | null | undefined): string | null | undefined {
  const raw = (input ?? "").trim();
  if (!raw) return null;
  const withScheme = /^https?:\/\//i.test(raw) ? raw.replace(/^https?:\/\//i, "https://") : `https://${raw}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== "https:" || !url.hostname.includes(".") || /\s/.test(raw)) return undefined;
    return withScheme.length <= 200 ? withScheme : undefined;
  } catch {
    return undefined;
  }
}

/** "https://www.cruzroja.org.py/x" → "cruzroja.org.py", for display. */
export function websiteHost(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}

// ─── Roles ──────────────────────────────────────────────────────────────────
export function canManageMembers(role: OrgRole | null | undefined): boolean {
  return role === "owner" || role === "admin";
}

/** Granting admin, transferring and deleting are owner-only in the RPCs. */
export function canGrantAdmin(role: OrgRole | null | undefined): boolean {
  return role === "owner";
}

/**
 * Medio and up ("contributors", is_org_contributor): post quests as the org,
 * upload files, create polls and invite people as Simple. Simple members only
 * read, chat and vote.
 */
export function canContribute(role: OrgRole | null | undefined): boolean {
  return role === "owner" || role === "admin" || role === "member";
}

/**
 * `/assistant?postAs=<orgId>` pre-selects that org in the quest card's "Post
 * as" (AgentBountyBridge). A convenience, not a permission: PostAsSelector
 * drops an org the viewer can't post as, and enforce_quest_organization
 * re-checks at insert.
 */
export const POST_AS_PARAM = "postAs";
export const postAsOrgHref = (orgId: string) => withPostAs("/assistant", orgId);
/** Add the org pre-selection to an /assistant URL that may already carry a goal. */
export const withPostAs = (assistantUrl: string, orgId: string) =>
  `${assistantUrl}${assistantUrl.includes("?") ? "&" : "?"}${POST_AS_PARAM}=${encodeURIComponent(orgId)}`;

/**
 * The levels the viewer may put on an invite, lowest first. Mirrors
 * create_org_invite / create_org_user_invite: Simple needs a contributor,
 * Medio an admin, Admin the owner. A link always admits as Simple, whoever
 * makes it — Medio and Admin go only through a person-addressed (user/email)
 * invite or the Members panel. Empty = may not invite at all.
 */
export function grantableInviteRoles(
  viewer: OrgRole | null | undefined,
  kind: OrgInvite["kind"] = "user",
): InviteRole[] {
  if (kind === "link") return canContribute(viewer) ? ["simple"] : [];
  switch (viewer) {
    case "owner": return ["simple", "member", "admin"];
    case "admin": return ["simple", "member"];
    case "member": return ["simple"];
    default: return [];
  }
}

/**
 * The levels the viewer may move `target` between (set_org_member_role),
 * lowest first. The owner sets any non-owner to any level; an admin moves
 * Simple ↔ Medio only, and never touches another admin. Empty = no picker.
 * Callers still exclude the viewer's own row (`memberActions`).
 */
export function assignableRoles(viewer: OrgRole | null | undefined, target: OrgRole): InviteRole[] {
  if (target === "owner") return [];
  if (viewer === "owner") return ["simple", "member", "admin"];
  if (viewer === "admin" && (target === "member" || target === "simple")) return ["simple", "member"];
  return [];
}

export interface MemberActions {
  changeRole: boolean;
  makeOwner: boolean;
  remove: boolean;
}

/**
 * What the viewer may do to one member row — the same rules the role RPCs
 * enforce: the owner changes any level and hands over ownership, an admin
 * moves and removes Simple/Medio members only, and nobody acts on
 * themselves or the owner.
 */
export function memberActions(viewerRole: OrgRole, member: OrgRole, isSelf: boolean): MemberActions {
  const owner = viewerRole === "owner";
  const other = !isSelf && member !== "owner";
  return {
    changeRole: other && assignableRoles(viewerRole, member).length > 0,
    makeOwner: owner && !isSelf,
    remove: other && (owner || (viewerRole === "admin" && (member === "member" || member === "simple"))),
  };
}

/**
 * May the viewer revoke this invite (revoke_org_invite: an admin, or the
 * invite's creator)? The list carries no creator id; org_invite_list unmasks
 * code / email hint / invitee only for admins and for the creator, so for a
 * non-admin an unmasked row is one of their own.
 */
export function canRevokeInvite(
  viewer: OrgRole | null | undefined,
  invite: Pick<OrgInvite, "code" | "emailHint" | "invitee">,
): boolean {
  if (canManageMembers(viewer)) return true;
  return canContribute(viewer) && (invite.code !== null || invite.emailHint !== null || invite.invitee !== null);
}

const ROLE_RANK: Record<OrgRole, number> = { owner: 0, admin: 1, member: 2, simple: 3 };

/** Owner, then admins, Medio, Simple; a stable sort keeps join order within each. */
export function sortMembersByRole<T extends { role: OrgRole }>(members: T[]): T[] {
  return [...members].sort((a, b) => ROLE_RANK[a.role] - ROLE_RANK[b.role]);
}

export function sortOrgsByName<T extends { name: string }>(orgs: T[]): T[] {
  return [...orgs].sort((a, b) => a.name.localeCompare(b.name));
}

// ─── Invites ────────────────────────────────────────────────────────────────
const DAY_MS = 86_400_000;

/** Whole days since an invite went out and until it expires, never negative. */
export function inviteTiming(
  invite: Pick<OrgInvite, "createdAt" | "expiresAt">,
  now: number,
): { sentDaysAgo: number; expiresInDays: number } {
  return {
    sentDaysAgo: Math.max(0, Math.round((now - Date.parse(invite.createdAt)) / DAY_MS)),
    expiresInDays: Math.max(0, Math.round((Date.parse(invite.expiresAt) - now) / DAY_MS)),
  };
}

// ─── Presence ───────────────────────────────────────────────────────────────
export interface PresenceLabel {
  key: string;
  count?: number;
  online: boolean;
}

/** i18n key (+ count) for a member's activity bucket; null = show nothing. */
export function presenceLabel(p: MemberPresence | undefined): PresenceLabel | null {
  if (!p) return null;
  switch (p.bucket) {
    case "active_now":
      return { key: "orgs.presence.activeNow", online: true };
    case "recent":
      return { key: "orgs.presence.recent", online: false };
    case "hours":
      return { key: "orgs.presence.hoursAgo", count: p.amount ?? 1, online: false };
    case "days":
      return { key: "orgs.presence.daysAgo", count: p.amount ?? 1, online: false };
    case "long_ago":
      return { key: "orgs.presence.longAgo", online: false };
    default:
      return null;
  }
}

// ─── Errors ─────────────────────────────────────────────────────────────────
const ERROR_KEYS: Record<string, string> = {
  org_slug_taken: "orgs.errors.slugTaken",
  org_slug_reserved: "orgs.errors.slugReserved",
  org_tier_required: "orgs.errors.tierRequired",
  org_invalid_input: "orgs.errors.invalidInput",
  org_invalid_email: "orgs.errors.invalidEmail",
  org_rate_limited: "orgs.errors.rateLimited",
  org_not_admin: "orgs.errors.notAdmin",
  org_owner_only: "orgs.errors.ownerOnly",
  org_not_member: "orgs.errors.notMember",
  org_not_contributor: "orgs.errors.notContributor",
  org_role_not_allowed: "orgs.errors.roleNotAllowed",
  org_already_member: "orgs.errors.alreadyMember",
  org_invite_invalid: "orgs.errors.inviteInvalid",
  org_invite_email_mismatch: "orgs.errors.inviteEmailMismatch",
  org_owner_cannot_leave: "orgs.errors.ownerCannotLeave",
  org_request_not_pending: "orgs.errors.requestNotPending",
  org_verified_name_locked: "orgs.errors.verifiedNameLocked",
  org_verified_website_locked: "orgs.errors.verifiedWebsiteLocked",
  org_verification_note_required: "orgs.errors.verificationNoteRequired",
  org_not_found: "orgs.errors.notFound",
  org_image_too_large: "orgs.errors.imageTooLarge",
  org_image_bad_type: "orgs.errors.imageBadType",
  org_image_upload_failed: "orgs.errors.imageUploadFailed",
  org_chat_rate_limited: "orgs.errors.chatRateLimited",
  org_poll_closed: "orgs.errors.pollClosed",
  org_poll_bad_option: "orgs.errors.invalidInput",
  org_not_in_audience: "orgs.errors.notInAudience",
  org_file_too_large: "orgs.errors.fileTooLarge",
  org_file_bad_type: "orgs.errors.fileBadType",
  org_file_upload_failed: "orgs.errors.fileUploadFailed",
  org_file_unavailable: "orgs.errors.fileUnavailable",
  org_not_quest_creator: "orgs.errors.notQuestCreator",
  org_quest_already_in_org: "orgs.errors.questAlreadyInOrg",
  org_quest_was_org_issued: "orgs.errors.questIssuedByOrg",
};

/** The RPCs raise stable codes; map them to i18n keys, never show raw text. */
export function orgErrorKey(error: unknown): string {
  const message = (error as { message?: unknown } | null)?.message;
  if (typeof message === "string") {
    const code = message.match(/\borg_[a-z_]+\b/)?.[0];
    if (code && ERROR_KEYS[code]) return ERROR_KEYS[code];
  }
  return "orgs.errors.generic";
}

/** `org_rate_check` puts `retry_at=<ISO UTC>` in the error's details. */
function rateLimitRetryAt(error: unknown): Date | null {
  const details = (error as { details?: unknown } | null)?.details;
  if (typeof details !== "string") return null;
  const iso = details.match(/\bretry_at=(\S+)/)?.[1];
  const at = iso ? new Date(iso) : null;
  return at && !Number.isNaN(at.getTime()) ? at : null;
}

/** The calendar day of `date` in `timeZone`, as YYYY-MM-DD. */
function dayIn(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
}

/**
 * orgErrorKey plus interpolation values. A rate-limited refusal names the
 * time the rolling 24h window frees up (today or tomorrow, never later),
 * instead of a blanket "try again tomorrow".
 */
export function orgErrorMessage(
  error: unknown,
  opts: { now: Date; locale: string; timeZone?: string },
): { key: string; values?: Record<string, string> } {
  const key = orgErrorKey(error);
  const retryAt = key === "orgs.errors.rateLimited" ? rateLimitRetryAt(error) : null;
  if (!retryAt || retryAt <= opts.now) return { key };
  const time = new Intl.DateTimeFormat(opts.locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: opts.timeZone,
  }).format(retryAt);
  const today = dayIn(retryAt, opts.timeZone) === dayIn(opts.now, opts.timeZone);
  return { key: today ? "orgs.errors.rateLimitedToday" : "orgs.errors.rateLimitedTomorrow", values: { time } };
}

// ─── Images ─────────────────────────────────────────────────────────────────
export const ORG_IMAGES_BUCKET = "org-images";
export type OrgImageKind = "logo" | "banner";

const EXT: Record<string, string> = { "image/webp": "webp", "image/png": "png", "image/jpeg": "jpg" };

/** `{orgId}/{kind}-{uuid}.{ext}` — the folder the bucket policy lets org admins write. */
export function orgImagePath(orgId: string, kind: OrgImageKind, mime: string, uuid: string): string {
  return `${orgId}/${kind}-${uuid}.${EXT[mime] ?? "img"}`;
}
